import { useEffect, useState } from "react";
import axios from "axios";
import * as Sentry from "@sentry/browser";
import { getApiUrl } from "@utils/baseUrl";
import type { ContactsRequestsResponse } from "@containers/Hermes/types";

// A message can carry several bulk-request blocks and each one wants the same
// conversation-wide response, so concurrent callers share one in-flight call.
const inFlight = new Map<string, Promise<ContactsRequestsResponse>>();

function fetchContactsRequests(conversationId: string): Promise<ContactsRequestsResponse> {
  let pending = inFlight.get(conversationId);
  if (!pending) {
    pending = axios
      .get<ContactsRequestsResponse>(getApiUrl(`hermes/conversations/${conversationId}/contacts-requests`))
      .then(({ data }) => data)
      .finally(() => inFlight.delete(conversationId));
    inFlight.set(conversationId, pending);
  }
  return pending;
}

type State = { data: ContactsRequestsResponse | null; failed: boolean };

/**
 * Where each bulk contacts request of a conversation stands, read live on
 * mount (conversation opened/reloaded) and whenever `refreshKey` changes. A
 * failed read yields `failed: true` — never treated as a failed request.
 */
export default function useContactsRequests(conversationId: string | null, refreshKey?: unknown): State {
  const [state, setState] = useState<State>({ data: null, failed: false });

  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    fetchContactsRequests(conversationId)
      .then((data) => !cancelled && setState({ data, failed: false }))
      .catch((error) => {
        Sentry.captureException(error);
        if (!cancelled) setState({ data: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, refreshKey]);

  return state;
}
